import React, { Fragment, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import { useDisclosure } from '@mantine/hooks'
import { useQuery, useQueryClient } from 'react-query'

import '../../css/breadCrumbs.css'
import {
  dividerVertical,
  divisaDireita,
  setaVoltar
} from '../../utils/alternanciaIcones'

import { getItemBreadCrumbDados, getTituloPagina } from './tituloPaginas'
import { api_gateway } from '../../conexoes/api'
import { desabilitarAcoesCliente } from '../../redux/actions'
import { useGlobal } from '../../stores/useGlobal'

import Botao from '../Mantine/Botao'
import Icone from '../Icone'
import Modal from '../Mantine/Modal'
import QuickButtons from '../QuickButtons'
import { ESimsService } from '../../services/http/eSims.service'

const AVAILABLE_ATALHOS = ['/veye/esim/dispositivos', '/veye/esim/estoque']

/* Bread Crumbs: Para futuras melhorias pode consultar: 
        https://developer.mozilla.org/pt-BR/docs/Web/CSS/Layout_cookbook/Breadcrumb_Navigation
*/

function BotaoVoltar() {
  const history = useHistory()

  return (
    <div onClick={() => history.goBack()} id="breadcrumbs-div-voltar">
      <img id="breadcrumbs-seta-voltar" src={setaVoltar()} alt="voltar" />
      <img
        id="breadcrumbs-divider-vertical"
        src={dividerVertical()}
        alt="divisor"
      />
    </div>
  )
}

const SetaDireita = () => (
  <img
    className="breadcrumbs-seta-direita"
    src={divisaDireita()}
    alt="seta direita"
  />
)

function BreadCrumbs() {
  const history = useHistory()
  const rota = history.location.pathname
  const { fetchFavActions, saveFavActions } = ESimsService
  const [opened, { open, close }] = useDisclosure(false)
  const despacho = useDispatch()
  const { setMostrarNotificacao, temItensTabelaSelecionados, setAcaoParaExecutar } = useGlobal()
  const quickButtonsRef = useRef(null)
  const queryClient = useQueryClient()
  const manejador = useSelector((state) => state.manejadorState.manejador)

  const breadCrumbs = useSelector(
    (state) => state.breadCrumbsState.breadCrumbsObj
  )

  const fetchAcoes = async (manejador) => {
    const response = await api_gateway.get(
      `/m1/consultar/acao?pa.n0_relevancia=1&pat.ds_acao=${manejador}`
    )
    return response.data
  }

  const fetchFavoritas = async () => {
    const response = await fetchFavActions(manejador)
    if (Array.isArray(response)) return { dados: response }
    if (response?.dados != null) return response
    
    return { dados: [] }
  }

  const salvarFavoritas = async (acao, favoritas) => {
    await saveFavActions(acao, favoritas.map((fav, index) => ({
      cd_pav_acao: Number(fav.cd_pav_acao ?? fav.id ?? 0),
      order: fav.order ?? index
    })))
  }

  const isRotaValida = AVAILABLE_ATALHOS.includes(rota)

  const queryAcoesEFavoritas = useQuery(
    ['acoes-e-favoritas', manejador],
    async () => {
      const [acoesResponse, favoritasResponse] = await Promise.all([
        fetchAcoes(manejador),
        fetchFavoritas()
      ])

      const acoes = acoesResponse?.dados || []
      const favoritas = favoritasResponse?.dados || []
      const acoesFiltradas = acoes.filter((acao) => acao.fl_relatorio === 0)

      const idsFavoritas = favoritas.map(
        (fav) => fav.cd_pav_acao || fav.id || fav.ds_acao_redux
      )
      const acoesSemFavoritas = acoesFiltradas.filter(
        (acao) =>
          !idsFavoritas.includes(acao.cd_pav_acao || acao.id || acao.ds_acao_redux)
      )

      return {
        acoes: acoesSemFavoritas,
        favoritas: favoritas
      }
    },
    {
      enabled: !!manejador && manejador !== 'fatDispositivos' && isRotaValida,
      staleTime: 0,
      cacheTime: 0,
      onError: () => {
        despacho(desabilitarAcoesCliente(false))
        setMostrarNotificacao({
          show: true,
          type: 'error',
          title: 'Erro ao carregar dados',
          message: 'Não foi possível carregar as ações e favoritas.'
        })
      },
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      retry: false
    }
  )

  if (rota === '/veye') return null

  if (!breadCrumbs || breadCrumbs?.length === 0) return null

  const breadCrumbArr = getItemBreadCrumbDados(breadCrumbs, rota)
  const tituloPagina = getTituloPagina(breadCrumbArr)

  const executarAcao = (acaoRedux) => {
    if (!acaoRedux) return

    setAcaoParaExecutar(acaoRedux)
  }

  return (
    <nav aria-label="Breadcumb" className="container-bread-crumbs">
      <BotaoVoltar />
      <div id="div-bread-crumbs">
        <h4 id="titulo-pagina">{tituloPagina}</h4>
        <div>
          <Link to="/veye" className="fonte-cor-18">
            Home
          </Link>

          {breadCrumbArr.map(({ rota, label }) => {
            const text = label === 'mensagens' ? tituloPagina : label

            return (
              <Fragment key={`${rota}=${label}`}>
                <SetaDireita />
                {rota ? (
                  <Link to={rota} className="span-bread-crumbs fonte-cor-18">
                    {text}
                  </Link>
                ) : (
                  <span className="span-bread-crumbs fonte-cor-18">{text}</span>
                )}
              </Fragment>
            )
          })}
        </div>
      </div>
      {AVAILABLE_ATALHOS.includes(rota) && (
        <div className="breadcrumbs-botao-container">
          {(queryAcoesEFavoritas?.data?.favoritas || [])
            .slice()
            .sort((a, b) => (a.order ?? a.ds_ordem ?? 999) - (b.order ?? b.ds_ordem ?? 999))
            .map((favorita) => ({
              id: favorita.cd_pav_acao,
              name: favorita.ds_acao,
              ds_acao_redux: favorita.ds_acao_redux,
              fl_selecionado: favorita.fl_selecionado
            }))
            .map((button) => {
              const deveDesabilitar =
                button.fl_selecionado === 0 && !temItensTabelaSelecionados

              return (
                <Botao
                  key={button.id}
                  label={button.name}
                  variant="primary"
                  uppercase={false}
                  disabled={deveDesabilitar}
                  onClick={() => {
                    executarAcao(button.ds_acao_redux)
                  }}
                />
              )
            })}
          <Botao
            label="Personalizar atalhos"
            variant="subtle"
            uppercase={false}
            onClick={() => open()}
            rightIcon={
              <Icone nome="edit" color="primary" width="20px" height="20px" />
            }
          />
        </div>
      )}
      {opened && (
        <Modal
          width="800px"
          type="default"
          titulo="Exibir botões de atalho"
          opened={opened}
          close={close}
          onConfirm={async () => {
            if (quickButtonsRef.current) {
              const resultado = await quickButtonsRef.current.salvar()
              if (resultado && resultado.sucesso) {
                try {
                  await salvarFavoritas(manejador, resultado.favoritasAtualizadas)
                  const dadosAtuais = queryAcoesEFavoritas.data
                  const favoritasComOrder = resultado.favoritasAtualizadas.map((fav, index) => ({
                    ...fav,
                    order: index
                  }))
                  if (dadosAtuais) {
                    queryClient.setQueryData(['acoes-e-favoritas', manejador], {
                      ...dadosAtuais,
                      favoritas: favoritasComOrder,
                      acao: manejador
                    })
                  }
                  await queryClient.invalidateQueries(['acoes-e-favoritas', manejador])
                  close()
                } catch (error) {
                  setMostrarNotificacao({
                    show: true,
                    type: 'error',
                    title: 'Erro ao salvar favoritos',
                    message: 'Não foi possível salvar as ações favoritas.'
                  })
                }
              } else {
                close()
              }
            } else {
              close()
            }
          }}
        >
          <QuickButtons
            ref={quickButtonsRef}
            acoesData={queryAcoesEFavoritas?.data?.acoes || []}
            favoritasData={queryAcoesEFavoritas?.data?.favoritas || []}
            manejador={manejador}
            isOpen={opened}
          />
        </Modal>
      )}
    </nav>
  )
}

export default BreadCrumbs
